/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useCreateEventContext } from "../../../context/CreateEventContext";
import { MARKETPLACE_SELLER_TYPES, BASIC_TIER_DOCUMENT_LIMIT } from "../../../constants/constants";
import CustomMultiSelect from "../../ui/DropDown";
import Button from "../../ui/Button";
import { TreePine, AlertCircle, Sparkles } from "lucide-react";
import DocumentNameAutocomplete from "../../ui/DocumentNameAutocomplete";
import { useAuth } from "../../../context/AuthContext";

const CreateEventContent2 = ({
  handleContinue,
  handlePrevious,
  handleSaveForLater,
}) => {
  const { t } = useTranslation();
  const { eventData, updateEventData } = useCreateEventContext();
  const { subscriptionTier } = useAuth();
  
  const isBasicTier = subscriptionTier === 'basic' || !subscriptionTier;
  const documentLimit = isBasicTier ? BASIC_TIER_DOCUMENT_LIMIT : Infinity;

  // Consolidate all state variables into one `useState`
  const [formState, setFormState] = useState({
    selectedSellerType: eventData.sellerTypes || [],
    sellerCapacity: eventData.sellerCapacity || "",
    documents: [{ documentName: "", requiredFrom: [], isRequired: true }],
    otherSellerType: eventData.otherSellerType || "",
    treeDonationEnabled: eventData.treeDonationEnabled || false,
    treeDonationRequired: eventData.treeDonationRequired || false,
  });

  const marketplaceSellerTypes = MARKETPLACE_SELLER_TYPES;

  const handleSelectAll = (e) => {
    setFormState((prev) => {
      const allSellerTypes = prev.otherSellerType
        ? [...marketplaceSellerTypes, prev.otherSellerType]
        : marketplaceSellerTypes;

      return {
        ...prev,
        selectedSellerType: e.target.checked ? allSellerTypes : [],
      };
    });
  };

  const handleOtherChange = (e) => {
    const { checked } = e.target;
    setFormState((prev) => {
      const updatedSelectedSellerType = checked
        ? [...new Set([...prev.selectedSellerType, prev.otherSellerType])] // Ensure uniqueness
        : prev.selectedSellerType.filter((type) => type !== prev.otherSellerType);

      return {
        ...prev,
        otherChecked: checked,
        otherSellerType: checked ? prev.otherSellerType : "",
        selectedSellerType: updatedSelectedSellerType,
      };
    });
  };

  const handleOtherInput = (e) => {
    const value = e.target.value;
    setFormState((prev) => {
      const updatedSelectedSellerType = prev.otherChecked
        ? [...prev.selectedSellerType.filter((type) => type !== prev.otherSellerType), value] // Replace old value
        : prev.selectedSellerType;

      return {
        ...prev,
        otherSellerType: value,
        selectedSellerType: updatedSelectedSellerType,
      };
    });
  };

  const handleSaveContinue = () => {
    handleContinue();
    updateEventData({
      sellerTypes: formState.selectedSellerType,
      otherSellerType: formState.otherSellerType,
      sellerCapacity: formState.sellerCapacity,
      documents: formState.documents,
      treeDonationEnabled: formState.treeDonationEnabled,
      treeDonationRequired: formState.treeDonationRequired,
    });
  };

  const handleSaveForLaterClick = () => {
    updateEventData({
      eventId: eventData.eventId,
      activeStep: 1,
      sellerTypes: formState.selectedSellerType,
      otherSellerType: formState.otherSellerType,
      sellerCapacity: formState.sellerCapacity,
      documents: formState.documents,
      treeDonationEnabled: formState.treeDonationEnabled,
      treeDonationRequired: formState.treeDonationRequired,
    });
    handleSaveForLater();
  };

  // Update formState dynamically
  const handleFormStateChange = (key, value) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDocumentChange = (index, field, value) => {
    const updatedDocuments = formState.documents.map((doc, i) =>
      i === index ? { ...doc, [field]: value } : doc
    );
    handleFormStateChange("documents", updatedDocuments);
  };

  const addDocument = () => {
    if (formState.documents.length >= documentLimit) {
      return;
    }
    handleFormStateChange("documents", [...formState.documents, { documentName: "", requiredFrom: [], isRequired: true }]);
  };

  const removeDocument = (indexToRemove) => {
    if (formState.documents.length <= 1) return;
    const updatedDocuments = formState.documents.filter((_, index) => index !== indexToRemove);
    handleFormStateChange("documents", updatedDocuments);
  };

  const hasReachedLimit = isBasicTier && formState.documents.length >= documentLimit;

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) => ({
        ...doc,
        requiredFrom: Array.isArray(doc.requiredFrom)
          ? doc.requiredFrom.filter((type) => prev.selectedSellerType.includes(type))
          : [],
      })),
    }));
  }, [formState.selectedSellerType]);

  useEffect(() => {
    if (eventData) {
      const docs = eventData.documents?.map(doc => ({
        ...doc,
        isRequired: doc.isRequired !== undefined ? doc.isRequired : true
      })) || [{ documentName: "", requiredFrom: [], isRequired: true }];
      
      setFormState({
        selectedSellerType: eventData.sellerTypes || [],
        sellerCapacity: eventData.sellerCapacity || "",
        documents: docs,
        otherSellerType: eventData.otherSellerType || "",
        treeDonationEnabled: eventData.treeDonationEnabled || false,
        treeDonationRequired: eventData.treeDonationRequired || false,
      });
    }
  }, [eventData]);

  useEffect(() => {
    const updatedDocuments = formState.documents.map((doc) => ({
      ...doc,
      requiredFrom: Array.isArray(doc.requiredFrom)
        ? doc.requiredFrom.filter((type) => formState.selectedSellerType.includes(type))
        : [],
    }));
    setFormState((prev) => ({ ...prev, documents: updatedDocuments }));
  }, [formState.selectedSellerType]);

  const handleSelectType = (e) => {
    const value = e.target.value;
    setFormState((prev) => ({
      ...prev,
      selectedSellerType: prev.selectedSellerType.includes(value)
        ? prev.selectedSellerType.filter((item) => item !== value)
        : [...prev.selectedSellerType, value],
    }));
  };
  console.log(formState.selectedSellerType)

  return (
    <div className="w-full h-full p-6 flex flex-col gap-8">
      <div className="">
        {/* Marketplace Vendor Types */}
        <div className="space-y-4">
          <label htmlFor="sellerCapacity" className="block text-sm font-medium text-grey-900">
            {t('createEvent.vendorTypes')}
          </label>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="selectAll"
                onChange={handleSelectAll}
                checked={formState.selectedSellerType.length === marketplaceSellerTypes.length}
                className="h-4 w-4 border-grey-300 rounded accent-primary-600"
              />
              <label htmlFor="selectAll" className="text-xs text-grey-900">{t('createEvent.selectAll')}</label>
            </div>
            {marketplaceSellerTypes.map((type) => (
              <div key={type} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value={type}
                  onChange={handleSelectType}
                  checked={formState.selectedSellerType.includes(type)}
                  className="h-4 w-4 border-grey-300 rounded accent-primary-600"
                />
                <label className="text-xs text-grey-900">{type}</label>
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="otherSellerType"
                checked={formState.otherChecked}
                onChange={handleOtherChange}
                disabled={!formState.otherSellerType.trim()}
                className="h-4 w-4 border-grey-300 accent-primary-600 rounded"
              />
              <label htmlFor="otherSellerType" className="text-xs text-grey-900">{t('createEvent.other')}</label>
              <input
                type="text"
                value={formState.otherSellerType}
                onChange={handleOtherInput}
                className="border border-grey-300 rounded p-1 text-sm"
                placeholder={t('createEvent.specifyOther')}
              />
            </div>
          </div>
        </div>

        {/* Seller Capacity Section */}
        <div className="space-y-2 mt-6">
          <label
            htmlFor="sellerCapacity"
            className="block text-sm font-medium text-grey-900"
          >
            {t('createEvent.sellerCapacity')}
          </label>
          <input
            type="number"
            className="w-4/12 p-2 border border-grey-300 rounded-lg"
            value={formState.sellerCapacity}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, sellerCapacity: e.target.value }))
            }
            placeholder={t('createEvent.sellerCapacityPlaceholder')}
          />
        </div>

        {/* Document Section */}
        <div className="space-y-4 mt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="block text-sm font-medium text-grey-900">{t('createEvent.requiredDocuments')}</label>
              <div className="flex items-center gap-1 px-2 py-0.5 bg-grey-100 rounded-full">
                <Sparkles className="h-3 w-3 text-grey-500" />
                <span className="text-xs text-grey-500 font-medium">{t('createEvent.smartSuggestions')}</span>
              </div>
            </div>
            {isBasicTier && (
              <span className={`text-xs font-medium ${hasReachedLimit ? 'text-amber-600' : 'text-grey-500'}`}>
                {formState.documents.length} / {documentLimit} documents
              </span>
            )}
          </div>

          {formState.documents.map((document, index) => (
            <div key={index} className="p-4 bg-grey-50 rounded-lg border border-grey-200">
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-grey-700">{t('createEvent.documentName')}</label>
                    <DocumentNameAutocomplete
                      value={document.documentName}
                      onChange={(value) => handleDocumentChange(index, "documentName", value)}
                      selectedSellerTypes={formState.selectedSellerType}
                      placeholder={t('createEvent.documentNamePlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor={`requiredFrom-${index}`}
                      className="block text-sm font-medium text-grey-700"
                    >
                      {t('createEvent.requiredFrom')}
                    </label>

                    <CustomMultiSelect
                      options={[t('createEvent.selectAll'), ...formState.selectedSellerType]}
                      selectedValues={document.requiredFrom}
                      onChange={(newValues) => handleDocumentChange(index, "requiredFrom", newValues)}
                      placeholder={t('createEvent.vendorTypesPlaceholder')}
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-grey-200">
                  <div className="flex gap-2">
                    {formState.documents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="h-[38px] px-3 flex items-center gap-2 text-grey-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors text-sm"
                        title={t('createEvent.remove')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {t('createEvent.remove')}
                      </button>
                    )}
                  </div>
                  {index === formState.documents.length - 1 && (
                    <Button 
                      onClick={addDocument} 
                      className="!h-[38px] !px-4 !py-0"
                      disabled={hasReachedLimit}
                    >
                      <span className="text-[16px] mr-1">+</span> {t('createEvent.addDocument')}
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Required/Optional Toggle */}
              <div className="mt-3 pt-3 border-t border-grey-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleDocumentChange(index, "isRequired", true)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      document.isRequired
                        ? 'bg-grey-700 text-white border border-grey-700'
                        : 'bg-white text-grey-500 border border-grey-200 hover:bg-grey-50'
                    }`}
                  >
                    {t('createEvent.required')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDocumentChange(index, "isRequired", false)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      !document.isRequired
                        ? 'bg-grey-700 text-white border border-grey-700'
                        : 'bg-white text-grey-500 border border-grey-200 hover:bg-grey-50'
                    }`}
                  >
                    {t('createEvent.optional')}
                  </button>
                </div>
                <span className="text-xs text-grey-500">
                  {document.isRequired
                    ? t('createEvent.vendorsMustSubmit')
                    : t('createEvent.vendorsCanChoose')}
                </span>
              </div>
            </div>
          ))}

          {hasReachedLimit && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">{t('createEvent.documentLimitReached')}</p>
                <p className="text-sm text-amber-700 mt-1">
                  {t('createEvent.documentLimitMessage', { limit: documentLimit })}
                </p>
                <button className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-800 underline">
                  {t('createEvent.viewUpgradeOptions')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tree Planting Option - Climate Action */}
        <div className="mt-6 p-4 bg-grey-50 border-l-4 border-l-green-600 border border-grey-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TreePine className="h-5 w-5 text-green-600" />
              <div>
                <span className="text-sm font-medium text-grey-900">{t('createEvent.treeDonation')}</span>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formState.treeDonationEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormState(prev => ({
                    ...prev,
                    treeDonationEnabled: enabled,
                    treeDonationRequired: enabled ? prev.treeDonationRequired : false
                  }));
                }}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-grey-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
          
          <p className="text-xs text-grey-500 mt-2 leading-relaxed">
            {t('createEvent.treeDonationDesc')}
          </p>
          
          {formState.treeDonationEnabled && (
            <div className="mt-3 pt-3 border-t border-grey-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formState.treeDonationRequired}
                  onChange={(e) => setFormState(prev => ({ ...prev, treeDonationRequired: e.target.checked }))}
                  className="h-4 w-4 border-grey-300 rounded accent-green-600"
                />
                <span className="text-sm text-grey-700">{t('createEvent.treeDonationRequireAll')}</span>
              </label>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        < div className="flex justify-end mt-6 gap-6" >
          <button
            onClick={handleSaveForLaterClick}
            className="px-6 py-3 text-grey-900 underline underline-offset-4"
          >
            {t('createEvent.saveForLater')}
          </button>
          <button
            onClick={handlePrevious}
            className="px-6 py-3 text-grey-900 border border-grey-300 rounded-lg"
          >
            {t('createEvent.previous')}
          </button>
          <button
            onClick={handleSaveContinue}
            className={`px-6 py-3 text-white rounded-lg ${formState.selectedSellerType.length > 0 && formState.sellerCapacity
              ? "bg-grey-700 hover:bg-grey-800"
              : "bg-grey-300 cursor-not-allowed"
              }`}
            disabled={
              formState.selectedSellerType.length === 0 || !formState.sellerCapacity
            }
          >
            {t('createEvent.saveAndContinue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEventContent2;